import { styled, Box, Button, Typography, keyframes } from "@mui/material";

// ─── Dr. Paw Color Palette ───────────────────────────────────────────
export const DP = {
  blue:        "#1565C0",
  blueMid:     "#1976D2",
  blueLight:   "#42A5F5",
  bluePale:    "#BBDEFB",
  blueBg:      "#F0F7FF",
  blueBgDeep:  "#E3F2FD",
  navy:        "#0A1628",
  navyprice:        "#103163",
  navyMid:     "#0D2137",
  navyLight:   "#142845",
  white:       "#FFFFFF",
  offWhite:    "#F8FAFE",
  textDark:    "#0A1628",
  textMid:     "#37474F",
  textMuted:   "#78909C",
  accent:      "#FF6B35",
  accentLight: "#FFF3ED",
  border:      "rgba(21,101,192,0.12)",
  borderLight: "rgba(21,101,192,0.06)",
  shadow:      "rgba(21,101,192,0.15)",
  shadowDeep:  "rgba(21,101,192,0.25)",
  glass:       "rgba(240,247,255,0.96)",
};

// ─── Keyframe Animations ────────────────────────────────────────────
export const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(32px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const fadeInDown = keyframes`
  from { opacity: 0; transform: translateY(-24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

export const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-60px); }
  to   { opacity: 1; transform: translateX(0); }
`;

export const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(60px); }
  to   { opacity: 1; transform: translateX(0); }
`;

export const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.88); }
  to   { opacity: 1; transform: scale(1); }
`;

export const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-12px); }
`;

export const pulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(21,101,192,0.4); }
  50%       { transform: scale(1.04); box-shadow: 0 0 0 12px rgba(21,101,192,0); }
`;

export const shimmer = keyframes`
  0%   { background-position: -1000px 0; }
  100% { background-position:  1000px 0; }
`;

export const scrollText = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

export const pulseDot = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.6; transform: scale(0.85); }
`;

export const waveIn = keyframes`
  0%   { transform: scaleX(0) translateX(-50%); opacity: 0; }
  100% { transform: scaleX(1) translateX(0);    opacity: 1; }
`;

export const counterUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

export const glow = keyframes`
  0%, 100% { box-shadow: 0 0 16px rgba(21,101,192,0.25); }
  50%       { box-shadow: 0 0 36px rgba(21,101,192,0.50); }
`;

export const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-8px); }
`;

export const fadeInStagger = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const morphShape = keyframes`
  0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
  25%       { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
  50%       { border-radius: 50% 60% 30% 60% / 30% 60% 70% 40%; }
  75%       { border-radius: 60% 40% 60% 40% / 70% 30% 50% 60%; }
`;

// ─── Styled Components ───────────────────────────────────────────────

export const PageWrapper = styled(Box)({
  backgroundColor: DP.white,
  minHeight: "80vh",
  color: DP.textDark,
  overflow: "hidden",
});

export const Section = styled(Box)(({ theme }) => ({
  padding: theme.spacing(12, 0),
  position: "relative",
  [theme.breakpoints.down("lg")]: { padding: theme.spacing(10, 0) },
  [theme.breakpoints.down("md")]: { padding: theme.spacing(8, 0) },
  [theme.breakpoints.down("sm")]: { padding: theme.spacing(6, 0) },
}));

export const SectionLabel = styled(Typography)({
  fontSize: "0.78rem",
  fontFamily: '"Inter", Helvetica, Arial, sans-serif',
  fontWeight: 700,
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  color: DP.blue,
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "6px 18px",
  backgroundColor: DP.blueBg,
  borderRadius: "50px",
  border: `1px solid ${DP.border}`,
  marginBottom: "16px",
});

export const PillButton = styled(Button)(({ theme }) => ({
  borderRadius: "50px",
  fontFamily: '"Inter", Helvetica, Arial, sans-serif',
  fontWeight: 700,
  textTransform: "none",
  fontSize: "1rem",
  padding: "12px 32px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: `0 12px 32px ${DP.shadowDeep}`,
  },
}));

export const OutlineButton = styled(Button)(({ theme }) => ({
  borderRadius: "50px",
  fontFamily: '"Inter", Helvetica, Arial, sans-serif',
  fontWeight: 700,
  textTransform: "none",
  fontSize: "1rem",
  padding: "11px 32px",
  border: `2px solid ${DP.blue}`,
  color: DP.blue,
  backgroundColor: "transparent",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: DP.blue,
    color: DP.white,
    transform: "translateY(-3px)",
    boxShadow: `0 12px 32px ${DP.shadow}`,
  },
}));

export const ServiceCard = styled(Box)(({ theme }) => ({
  backgroundColor: DP.white,
  borderRadius: "20px",
  padding: theme.spacing(4),
  border: `1px solid ${DP.border}`,
  transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
  height: "100%",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0, left: 0, right: 0,
    height: "3px",
    background: `linear-gradient(90deg, ${DP.blue}, ${DP.blueLight})`,
    transform: "scaleX(0)",
    transformOrigin: "left",
    transition: "transform 0.35s ease",
    borderRadius: "20px 20px 0 0",
  },
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: `0 24px 60px ${DP.shadow}`,
    borderColor: DP.bluePale,
    "&::before": { transform: "scaleX(1)" },
  },
}));

export const TestimonialCard = styled(Box)(({ theme }) => ({
  backgroundColor: DP.white,
  borderRadius: "20px",
  padding: theme.spacing(4),
  border: `1px solid ${DP.border}`,
  boxShadow: `0 4px 24px ${DP.borderLight}`,
  transition: "all 0.3s ease",
  height: "100%",
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: `0 20px 48px ${DP.shadow}`,
    borderColor: DP.bluePale,
  },
}));

export const ScrollingTextContainer = styled(Box)({
  width: "100%",
  overflow: "hidden",
  backgroundColor: DP.navy,
  padding: "18px 0",
  position: "relative",
});

export const ScrollingTextTrack = styled(Box)({
  display: "flex",
  gap: "48px",
  animation: `${scrollText} 35s linear infinite`,
  whiteSpace: "nowrap",
});

export const ScrollingTextItem = styled(Typography)({
  fontSize: "0.88rem",
  fontFamily: '"Inter", Helvetica, Arial, sans-serif',
  fontWeight: 600,
  color: "rgba(255,255,255,0.85)",
  textTransform: "uppercase",
  letterSpacing: "0.18em",
  display: "inline-flex",
  alignItems: "center",
  gap: "48px",
  "&::after": {
    content: '"🐾"',
    marginLeft: "48px",
    fontSize: "0.85rem",
    opacity: 0.6,
  },
});

export const HeroSection = styled(Box)(({ theme }) => ({
  minHeight: "85vh",
  display: "flex",
  alignItems: "center",
  position: "relative",
  overflow: "hidden",
  paddingTop: "80px",
  background: `linear-gradient(135deg, ${DP.navy} 0%, ${DP.navyMid} 50%, ${DP.navyLight} 100%)`,
  [theme.breakpoints.down("md")]: {
    minHeight: "auto",
    paddingTop: "100px",
    paddingBottom: "60px",
  },
}));

export const PricingCard = styled(Box)(({ theme }) => ({
  backgroundColor: DP.white,
  borderRadius: "24px",
  padding: theme.spacing(4),
  border: `1px solid ${DP.border}`,
  transition: "all 0.4s ease",
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: `0 24px 60px ${DP.shadow}`,
  },
}));

export const ImageCard = styled(Box)({
  borderRadius: "24px",
  overflow: "hidden",
  position: "relative",
  transition: "all 0.4s ease",
  "& img": { transition: "transform 0.6s ease" },
  "&:hover img": { transform: "scale(1.04)" },
  "&:hover": { boxShadow: `0 32px 80px ${DP.shadow}` },
});

export const GradientText = styled("span")({
  background: `linear-gradient(135deg, ${DP.blueLight}, ${DP.blue})`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  display: "inline",
});
